import moment from 'moment'
import AuthMixin from '@/components/mixins/auth-mixin'
import OnEnterMixin from '@/components/mixins/onenter-mixin'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'

export default {
  name: 'reportingSearchBar',
  inject: [
    '$utils',
    '$dataStore',
    '$api',
    '$apiError'
  ],
  components: {},
  props: {
    reportProp: {
      type: Object,
      default () { return {} }
    },
    selectedDateFilterProp: {
      default () { return null }
    },
    playerSelectedFromParentProp: {
      default () { return [] }
    },
    tagsProp: {
      type: Array,
      default () { return [] }
    },
    isActive: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    reportProp (newVal) {
      this.presentReportByBatch = newVal.batchHistory
      this.originalPresentPlayerListBeforeTheSearch = this.$utils.clone(newVal)
    },
    searchTextVal (val) {
      this.searchTextVal = val
      this.handlePreFlightSearchFromPresentData()
      this.confirmedSearchedValFlag = false
    },
    playerStatusSelected: {
      handler (val) {
        this.confirmedSearchedValFlag = false
      },
      deep: true
    },
    playerSelectedFromParentProp: {
      handler (val) {
        this.playerSelectedFromParent = val
      },
      deep: true
    },
    selectedDateFilterProp (newVal) {
      this.selectedDateFilter = newVal
    },
    tagsProp: {
      handler (newTagsVal) {
        this.tags = newTagsVal

        const selectedTagsFromSlider = this.$dataStore.getStore().selectedTagsFromSlider ? this.$dataStore.getStore().selectedTagsFromSlider : {}
        if (selectedTagsFromSlider.reportingPage && selectedTagsFromSlider.reportingPage.length) {
          this.tags = selectedTagsFromSlider.reportingPage
        } else {
          this.tags = []
        }

        if (this.tags.length <= 0) return

        if (this.originalPresentPlayerListBeforeTheSearch === null) {
          this.originalPresentPlayerListBeforeTheSearch = this.reportProp
        }
        this.handlePreFlightSearchFromPresentData()

        // Fetch from backend if user has seconds idle time in type selection
        let timer = null
        clearTimeout(timer)
        timer = setTimeout(() => {
          this.fetchPlayerListFromBackend({
            onKeyupIdleTimeRequest: true,
            callback: () => {
              this.$utils.mainLoader(false)
            }
          })
        }, this.idleTimeSearchingVal)
      },
      deep: true
    }
  },
  data () {
    return {
      error: '',
      searchStatus: { confirmedSearch: 'confirmedSearch', unConfirmedSearch: 'unConfirmedSearch' },
      originalPresentPlayerListBeforeTheSearch: null,
      presentReportByBatch: this.reportProp.batchHistory,
      playerSelectedFromParent: this.playerSelectedFromParentProp,
      enableSearchOptions: false,
      searchTextVal: '',
      searchResultsFromFrontend: { fullDetailsList: [], serialNumberList: [] },
      searchResultsFromBackend: { fullDetailsList: [] },
      searchResultsSummary: [],
      allowPlayerStatusSelectionInConfirmedSearch: false,
      playerStatusSelected: [],
      confirmedSearchedValFlag: false,
      idleTimeSearchingVal: 2000, // 2 seconds,
      mouseOverConfirmedText: false,
      tags: this.tagsProp,
      selectedDateFilter: this.selectedDateFilterProp
    }
  },
  mixins: [AuthMixin, OnEnterMixin],
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('reporting-search-bar-change', { action, payload: { data } })
      }
    },
    handleSearchIconClick () {
      if (this.presentReportByBatch.length < 1) return
      useMixpanel().track('Search clicked')

      this.originalPresentPlayerListBeforeTheSearch = this.reportProp
      this.enableSearchOptions = true
      this.$nextTick(_ => { this.$refs.playerSearchInput.focus() })
    },
    fetchPlayerListFromBackend (params) {
      const apiRequest = () => {
        const requestParams = { max: 6 }

        // Set search value
        if (this.searchTextVal !== '') {
          requestParams.search = this.searchTextVal
          useMixpanel().track('Search text')
        }

        // set tags
        if (this.tags.length > 0) {
          requestParams.tags = JSON.stringify(this.tags)
        }

        if (!params.onKeyupIdleTimeRequest) {
          this.$utils.mainLoader(false)
        } else {
          this.$utils.mainLoader(true)
        }

        // Selected date
        if (this.selectedDateFilter) {
          const dateFilter = moment(this.selectedDateFilter)
          requestParams.start = dateFilter.clone().startOf('month').toISOString()
          requestParams.end = dateFilter.clone().endOf('month').toISOString()
        }

        this.error = ''
        this.$api.get(`${CONFIG.API_BASE_URL}/report/player-activity`, { params: requestParams })
          .then(response => {
            const newSearchResults = response.data
            const processSearchResults = (newPlayerBatch) => {
              // Check previous and current params if results still valid for UI
              const requestTags = response.config.params && response.config.params.tags ? response.config.params.tags : null
              const selectedTags = (Array.isArray(this.tags) && this.tags.length) ? JSON.stringify(this.tags) : null
              const reqStartDate = (response.config.params && response.config.params.start) ? response.config.params.start : null
              const reqEndDate = (response.config.params && response.config.params.end) ? response.config.params.end : null
              let isTheSameDateFilter = true

              if (!this.selectedDateFilter && reqStartDate) {
                isTheSameDateFilter = false
              } else if (this.selectedDateFilter) {
                const dateFilter = moment(this.selectedDateFilter)
                if (dateFilter.clone().startOf('month').toISOString() !== reqStartDate) {
                  isTheSameDateFilter = false
                } else if (dateFilter.clone().endOf('month').toISOString() !== reqEndDate) {
                  isTheSameDateFilter = false
                }
              }

              /** Process search results
               - Request tags and current tags selection is the same
               - Make sure no selection have been made in parent component to avoid data refresh
               - Make sure current seach options is the same with the search options before the request to avoid flicker
              */
              if (requestTags === selectedTags && isTheSameDateFilter) {
                if (params.onKeyupIdleTimeRequest) {
                  if (!this.playerSelectedFromParent.length && (this.enableSearchOptions || this.tags.length > 0)) {
                    const prevSearchTextVal = (response.config.params.search) ? response.config.params.search : ''
                    const prevFileType = (response.config.params.type) ? response.config.params.type : ''
                    const currentSearchTextVal = this.searchTextVal
                    const currentFileType = this.playerStatusSelected.join(',')

                    if (prevSearchTextVal === currentSearchTextVal && prevFileType === currentFileType) {
                      this.searchResultsFromBackend.fullDetailsList = newPlayerBatch
                      this.sendSearchResultsToParent({ afterAPIRequest: true })
                    }
                  }
                }

                if (!params.onKeyupIdleTimeRequest) {
                  this.searchResultsFromBackend.fullDetailsList = newPlayerBatch
                  this.sendSearchResultsToParent({ afterAPIRequest: true })
                }
              }

              // Process callback
              if ((typeof (params) !== 'undefined') && (params.callback)) {
                params.callback()
              } else {
                this.$utils.mainLoader(false)
              }
            }

            const validateMoreItems = (serialNumber) => {
              const valRequestParams = this.$utils.clone(requestParams)
              valRequestParams.max = 1
              valRequestParams.exclusiveStartSerialNumber = serialNumber

              this.$api.get(`${CONFIG.API_BASE_URL}/report/player-activity`, { params: valRequestParams })
                .then(response => {
                  if (response.data.monitoring && !response.data.monitoring.length) {
                    newSearchResults.monitoring[newSearchResults.monitoring.length - 1].moreItems = false
                  }
                  processSearchResults(newSearchResults)
                })
                .catch((error) => {
                  this.error = error.message
                  this.$utils.mainLoader(false)
                  this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { validateMoreItems() } })
                })
            }

            // Check moreItems on the last item and validate if really has more items from backend
            if (newSearchResults.monitoring.length && newSearchResults.monitoring[newSearchResults.monitoring.length - 1].moreItems) {
              validateMoreItems(newSearchResults.monitoring[newSearchResults.monitoring.length - 1].serialNumber)
            } else {
              processSearchResults(newSearchResults)
            }
          })
          .catch((error) => {
            this.error = error.message
            this.$utils.mainLoader(false)
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
      }
      apiRequest()
    },
    handleSearchConfirmation (actionType) {
      const searchTextVal = this.searchTextVal.trim()
      const hasSearchText = searchTextVal.length > 0
      const hasPlayerStatusSelected = this.playerStatusSelected.length > 0
      const isOnEnterOrOnBlur = ['onEnter', 'onBlur'].indexOf(actionType) >= 0
      const isOnSelectPlayerStatus = actionType === 'selectPlayerStatus'
      let searchFromBackend = false

      if (isOnEnterOrOnBlur) {
        if (!hasSearchText && !hasPlayerStatusSelected) {
          this.handleResetSearchValue()
          this.enableSearchOptions = false
          return
        }

        /* If user click on the file type BEFORE entering any text, then apply the file filter and let user type text
        (and then confirm the search using enter or focus out) */
        if (hasSearchText && hasPlayerStatusSelected) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }

        if (!hasSearchText && hasPlayerStatusSelected) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }

        /* If the user enter some text and press enter, then show the files filters and if the user clicks on one of them,
        apply it together with the text previously entered by the user.  */
        if (hasSearchText && !hasPlayerStatusSelected) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }
      }

      if (isOnSelectPlayerStatus) {
        if (!hasPlayerStatusSelected && !hasSearchText) {
          this.handleResetSearchValue()
          return
        }

        /* If the user click on the file type when some text have been entered, then validate the text and show the tag
          but let the files filter visible and apply the file filter the user
        */
        if (hasPlayerStatusSelected && hasSearchText) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }
      }

      if (searchFromBackend && this.confirmedSearchedValFlag) {
        return
      }

      // Fetch from backend
      if (searchFromBackend) {
        this.confirmedSearchedValFlag = true
        this.fetchPlayerListFromBackend({
          callback: () => {
            this.$utils.mainLoader(false)

            if (this.playerStatusSelected.length === 0) {
              this.allowPlayerStatusSelectionInConfirmedSearch = true
            }
          }
        })
      }
    },
    /**
     * Handle client side search from present data fetch from backend or called as listByBatch
     */
    handlePreFlightSearchFromPresentData () {
      const dataSource = this.$utils.clone(this.originalPresentPlayerListBeforeTheSearch.batchHistory)
      const preflightSearchResults = {
        fullDetailsList: {
          accounting: [],
          media: [],
          monitoring: []
        },
        serialNumberList: []
      }
      const searchText = this.searchTextVal.trim()
      const searchByName = (searchText.length)
      const searcyByNameAndType = (searchText.length > 0 && this.tags.length === 0)
      const searcyByNameAndTags = (searchText.length > 0 && this.tags.length > 0)
      const searchByTags = (searchText.length === 0 && this.tags.length > 0)
      const hasMultiTagSelection = this.tags.length > 1
      const hasSingleTagSelection = this.tags.length === 1

      for (const activityByBatch of dataSource) {
        const activity = activityByBatch

        for (const playerIndx in activity.monitoring) {
          const playerItem = activity.monitoring[playerIndx]
          const playerIncludesName = playerItem.name && playerItem.name.toLowerCase().includes(searchText.toLowerCase())
          let playerIncludesTag = false

          // Check tags
          if (!playerItem.tags) playerItem.tags = []

          if (hasMultiTagSelection) {
            playerIncludesTag = this.tags.every((selectedTag) => { return playerItem.tags.includes(selectedTag) })
          } else if (hasSingleTagSelection) {
            for (const indx in playerItem.tags) {
              if (this.tags[0] === playerItem.tags[indx]) {
                playerIncludesTag = true
                break
              }
            }
          } else {
            playerIncludesTag = false
          }

          let addToResult = false

          if (searchByName && playerIncludesName) { // Search by name
            addToResult = true
          } else if (searchByTags && playerIncludesTag) {
            addToResult = true
          } else if (searcyByNameAndType && playerIncludesName) {
            addToResult = true
          } else if (searcyByNameAndTags && (playerIncludesName && playerIncludesTag)) {
            addToResult = true
          }

          if (addToResult) {
            preflightSearchResults.fullDetailsList.monitoring.push(playerItem)
            preflightSearchResults.serialNumberList.push(playerItem.serialNumber) // Add to monitoring
            // Include media
            preflightSearchResults.fullDetailsList.media = preflightSearchResults.fullDetailsList.media.concat(activity.media)
            // Include accounting
            preflightSearchResults.fullDetailsList.accounting = preflightSearchResults.fullDetailsList.accounting.concat(activity.accounting)
          }
        }
      }
    },
    sendSearchResultsToParent (params) {
      if (!this.enableSearchOptions && this.allowPlayerStatusSelectionInConfirmedSearch) {
        this.searchResultsFromFrontend = { fullDetailsList: [], serialNumberList: [] }
      }

      const searchResultsFromFrontend = this.$utils.clone(this.searchResultsFromFrontend)
      const searchResultsFromBackend = this.$utils.clone(this.searchResultsFromBackend)
      const searchResultsSummary = searchResultsFromFrontend

      searchResultsSummary.fullDetailsList = searchResultsFromBackend.fullDetailsList
      searchResultsFromBackend.fullDetailsList.monitoring.forEach((playerItemBackend) => {
        if (searchResultsFromFrontend.serialNumberList.indexOf(playerItemBackend.serialNumber) < 0) {
          searchResultsSummary.serialNumberList.push(playerItemBackend.serialNumber)
        }
      })

      this.emitEvent('NEW_PLAYER_SEARCH_RESULTS', {
        searchResults: searchResultsSummary,
        reqSearchFilter: { search: this.searchTextVal, type: this.playerStatusSelected },
        afterAPIRequest: (typeof params === 'object' && params.afterAPIRequest)
      })
    },
    handleResetSearchValue () {
      this.searchTextVal = ''
      this.enableSearchOptions = true
      this.allowPlayerStatusSelectionInConfirmedSearch = false
      this.playerStatusSelected = []
      this.resetToOriginalPresentData()
    },
    resetToOriginalPresentData () {
      // Reset results from list history
      const originalPlayerList = this.$utils.clone(this.originalPresentPlayerListBeforeTheSearch)
      this.emitEvent('RESET_TO_ORIGINAL_LISTS', {
        originalPlayerList,
        reqSearchFilter: { search: null, type: [] }
      })
    },
    handleSearhOptionOnBlur () {
      const self = this
      $('#reportingPage').on('click', (e) => {
        const targetElement = $(e.target)
        const preventConfirmedSearch = targetElement.hasClass('prevent-confirmed-search') || targetElement.hasClass('page-item') || targetElement.hasClass('download-icon')
        const forceToCloseSearchOption = targetElement.hasClass('player-item-cover')

        if (self.enableSearchOptions && !preventConfirmedSearch && !forceToCloseSearchOption) {
          self.handleSearchConfirmation('onBlur')
        } else if (forceToCloseSearchOption) {
          if (self.searchTextVal.length > 0) {
            self.enableSearchOptions = true
          }
        }
      })
    },
    /**
     * Auto search from backend after seconds when user stop typing
     */
    handleAutoSearchFromBackendWhenStopTyping () {
      let timer = null
      this.$refs.reportingSearchBar.onkeyup = () => {
        clearTimeout(timer)
        timer = setTimeout(() => {
          this.fetchPlayerListFromBackend({
            onKeyupIdleTimeRequest: true,
            callback: () => {
              this.$utils.mainLoader(false)
            }
          })
        }, this.idleTimeSearchingVal)
      }
    },
    // Auto search confirmation if press enter even if the input search field is not onFocus state
    handleSearchConfirmatioOnPagePressEnter () {
      const self = this
      $(document).keypress((event) => {
        const keycode = (event.keyCode ? event.keyCode : event.which)
        if (keycode.toString() === '13' && self.enableSearchOptions) {
          self.handleSearchConfirmation('onEnter')
        }
      })
    },
    handleConfirmedTextValueClick () {
      this.enableSearchOptions = true
    },
    onEnter (event) {
      if (event.target.id === 'player-reporting-search-input') {
        this.handleSearchConfirmation('onEnter')
      }
    }
  },
  mounted () {
    this.handleSearhOptionOnBlur()
    this.handleAutoSearchFromBackendWhenStopTyping()
    this.handleSearchConfirmatioOnPagePressEnter()
  }
}
